import { useEffect, useState, useCallback } from 'react';
import { websocketService } from '@/services/websocket';
import { WebSocketMessage } from '@/types';

export function useWebSocket(key?: string) {
  const [isConnected, setIsConnected] = useState(websocketService.isConnected());
  const [lastMessage, setLastMessage] = useState<WebSocketMessage | null>(null);
  const [messages, setMessages] = useState<WebSocketMessage[]>([]);

  useEffect(() => {
    // Subscribe to connection status
    const unsubscribeConnection = websocketService.onConnection((connected) => {
      setIsConnected(connected);
    });

    // Subscribe to messages
    const messageKey = key || '*';
    const MAX_MESSAGES = 500;
    const unsubscribeMessage = websocketService.onMessage(messageKey, (message) => {
      setLastMessage(message);
      setMessages((prev) => {
        const next = [...prev, message];
        return next.length > MAX_MESSAGES ? next.slice(-MAX_MESSAGES) : next;
      });
    });

    return () => {
      unsubscribeConnection();
      unsubscribeMessage();
    };
  }, [key]);

  const sendMessage = useCallback((event: string, data: any) => {
    websocketService.emit(event, data);
  }, []);

  const clearMessages = useCallback(() => {
    setMessages([]);
    setLastMessage(null);
  }, []);

  return {
    isConnected,
    lastMessage,
    messages,
    sendMessage,
    clearMessages,
  };
}

export function useApplicationWebSocket(applicationId: string) {
  const [buildStatus, setBuildStatus] = useState<string>('idle');
  const [deploymentStatus, setDeploymentStatus] = useState<string>('idle');
  const [logs, setLogs] = useState<string[]>([]);

  useEffect(() => {
    if (!applicationId) return;

    // Subscribe to application updates
    websocketService.subscribeToApplication(applicationId);

    const unsubscribeBuild = websocketService.onMessage(`app:${applicationId}`, (message) => {
      if (message.type === 'build') {
        setBuildStatus(message.data.status);
      } else if (message.type === 'deployment') {
        setDeploymentStatus(message.data.status);
      } else if (message.type === 'log') {
        const MAX_LOGS = 1000;
        setLogs((prev) => {
          const next = [...prev, message.data];
          return next.length > MAX_LOGS ? next.slice(-MAX_LOGS) : next;
        });
      }
    });

    return () => {
      websocketService.unsubscribeFromApplication(applicationId);
      unsubscribeBuild();
    };
  }, [applicationId]);

  const clearLogs = useCallback(() => {
    setLogs([]);
  }, []);

  return {
    buildStatus,
    deploymentStatus,
    logs,
    clearLogs,
  };
}

export function useBuildJobWebSocket(jobId: string | undefined) {
  const [job, setJob] = useState<any>(null);
  const [logs, setLogs] = useState<string[]>([]);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (!jobId) return;

    websocketService.subscribeToBuildJob(jobId);

    const unsubscribe = websocketService.onMessage(`job:${jobId}`, (message) => {
      if (message.type === 'build') {
        setJob(message.data);
        setProgress(message.data.progress || 0);
        if (message.data.logs) {
          setLogs(message.data.logs);
        }
      } else if (message.type === 'log') {
        setLogs((prev) => [...prev, message.data]);
      }
    });

    return () => {
      websocketService.unsubscribeFromBuildJob(jobId);
      unsubscribe();
    };
  }, [jobId]);

  return {
    job,
    logs,
    progress,
  };
}